import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { Modal } from "@/components";
import { ButtonColor } from "@/components/Button/types";

/** What the customer keeps access to after cancelling, resolved before the cancellation happens. */
export interface ExpectedCancellationEnd {
	/** Formatted end of the current billing period — the trial end while the subscription trials. */
	currentPeriodEndFormatted: string | null;
	hasDiscount: boolean;
	discountLabel: string | null;
	discountEndFormatted: string | null;
}

interface CancelSubscriptionModalProps {
	isOpen: boolean;
	isCancelling: boolean;
	/** Cancelling during the trial ends it at the trial end and never charges — own wording. */
	isTrialing: boolean;
	expectedEnd: ExpectedCancellationEnd | null;
	error: string | null;
	onClose: () => void;
	onConfirm: () => void;
}

const NOTICE_BASE = "usage.subscription.overview.";

/**
 * Picks the i18n key describing when access ends, so each variant stays a single translatable
 * sentence instead of a run of conditionally concatenated fragments.
 */
const resolveNoticeKey = (isTrialing: boolean, expectedEnd: ExpectedCancellationEnd | null) => {
	if (isTrialing) {
		return expectedEnd?.currentPeriodEndFormatted ? "cancelNoticeTrial" : "cancelNoticeTrialNoDate";
	}

	if (expectedEnd?.hasDiscount) {
		if (expectedEnd.discountLabel && expectedEnd.discountEndFormatted) {
			return "cancelNoticeDiscountLabelAndEnd";
		}

		if (expectedEnd.discountEndFormatted) {
			return "cancelNoticeDiscountEnd";
		}

		return expectedEnd.discountLabel ? "cancelNoticeDiscountLabel" : "cancelNoticeDiscount";
	}

	return expectedEnd?.currentPeriodEndFormatted
		? "cancelNoticeBilling"
		: "cancelNoticeBillingNoDate";
};

/**
 * Confirmation dialog for cancelling a subscription. Also reachable during the trial (PREC-3757) —
 * without it a trial customer has no self-serve way out and has to email support.
 */
export const CancelSubscriptionModal: FunctionComponent<CancelSubscriptionModalProps> = ({
	isOpen,
	isCancelling,
	isTrialing,
	expectedEnd,
	error,
	onClose,
	onConfirm,
}) => {
	const { t } = useTranslation();

	const noticeKey = resolveNoticeKey(isTrialing, expectedEnd);

	return (
		<Modal
			handleClose={onClose}
			handleSave={onConfirm}
			isLoading={isCancelling}
			isOpened={isOpen}
			submitButtonColor={ButtonColor.ACTION}
			submitButtonText={t(`${NOTICE_BASE}cancelSubscription`)}
			title={t(`${NOTICE_BASE}cancelSubscription`)}
		>
			<div className="flex flex-col gap-3">
				<p className="text-sm">
					{t(`${NOTICE_BASE}${isTrialing ? "cancelConfirmTrial" : "cancelConfirm"}`)}
				</p>
				<div className="text-sm bg-status-warning-light border border-status-warning rounded-md p-3">
					{t(`${NOTICE_BASE}${noticeKey}`, {
						date: expectedEnd?.currentPeriodEndFormatted ?? "",
						label: expectedEnd?.discountLabel ?? "",
						discountEnd: expectedEnd?.discountEndFormatted ?? "",
					})}{" "}
					{t(`${NOTICE_BASE}cancelNoticeDataRetention`)}
				</div>
				{error && (
					<div className="text-sm text-status-error bg-status-error-light border border-status-error rounded-md p-3">
						{error}
					</div>
				)}
			</div>
		</Modal>
	);
};
